import { useCallback, useMemo } from 'react';
import { parseISO } from 'date-fns';
import { getIn, useFormikContext } from 'formik';
import type { ChangeEvent } from 'react';

import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import FormHelperText from '@mui/material/FormHelperText';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DatePicker } from '@mui/x-date-pickers';

import { getDocumentName, graphql } from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';
import { toFirstNameCase, toLastNameCase } from '@e-socna/third-parties';
import type { CivilityId, ThirdPartyCategoryId } from '@e-socna/data-layer';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import { useReferencesContext } from '../../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ImageUploadField } from '../../../../components/ImageUploadField';
import { PersonPage_OnePerson_QueryDocument } from '../../../../pages/Person/hooks/useFindOnePersonQuery.hook';
import { IdMultiSelect, IdSelect } from '../../../../components/IdSelect';
import type { IdOption } from '../../../../components/IdSelect';

import type { ThirdPartyForm_Person } from '../../fragments/person.fragment';

export const ThirdPartyForm_PersonGeneralInfoStep_Person_Fragment = graphql(`
  fragment ThirdPartyForm_PersonGeneralInfoStep_Person_Fragment on Person {
    code
    internalMemo
    memo
    birthDate
    isVip
    color
    categoryIds
    firstName
    lastName
    civilityId
    signatureImageUri
    pictureImageUri
  }
`);

interface Props {
  readonly id: string;
}

// eslint-disable-next-line no-empty-pattern
export const ThirdPartyPersonGeneralInfoFormStep = ({}: Readonly<Props>) => {
  const {
    thirdPartyCategories,
    civilities,
    fileDirectoryCategoriesCodeMap,
    thirdPartyFileCategoriesCodeMap,
  } = useReferencesContext();

  const {
    errors,
    handleBlur,
    handleChange,
    setFieldTouched,
    setFieldValue,
    touched,
    values,
  } = useFormikContext<ThirdPartyForm_Person>();

  // The back normalizes the names: their case is shown while typing. The
  // input keeps its caret, the value being changed in place.
  const nameChangeHandlerFactory = useCallback(
    (toNameCase: (text: string) => string) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        const input = event.target;
        const { selectionStart, selectionEnd } = input;

        // eslint-disable-next-line functional/immutable-data
        input.value = toNameCase(input.value);
        input.setSelectionRange(selectionStart, selectionEnd);

        handleChange(event);
      },
    [handleChange],
  );
  const handleFirstNameChange = useMemo(
    () => nameChangeHandlerFactory(toFirstNameCase),
    [nameChangeHandlerFactory],
  );
  const handleLastNameChange = useMemo(
    () => nameChangeHandlerFactory(toLastNameCase),
    [nameChangeHandlerFactory],
  );

  const switchAutoColor = useCallback(
    (_event: Readonly<ChangeEvent<HTMLInputElement>>, checked: boolean) => {
      if (checked) {
        return;
      }

      void setFieldValue('color', '#ffffff');
    },
    [setFieldValue],
  );

  const clearColor = useCallback(
    () => void setFieldValue('color', undefined),
    [setFieldValue],
  );

  const handleBirthDateChange = useCallback(
    (date: unknown) => void setFieldValue('birthDate', date),
    [setFieldValue],
  );

  const handleBirthDateBlur = useCallback(
    () => void setFieldTouched('birthDate', true),
    [setFieldTouched],
  );

  const birthDateValue = useMemo(
    () =>
      typeof values.birthDate === 'string'
        ? parseISO(values.birthDate)
        : values.birthDate,
    [values.birthDate],
  );

  const handleCivilityIdChange = useCallback(
    (id: CivilityId | typeof EMPTY_STRING) => {
      void setFieldValue('civilityId', id);
    },
    [setFieldValue],
  );

  const handleCategoryIdsChange = useCallback(
    (ids: readonly ThirdPartyCategoryId[]) => {
      void setFieldValue('categoryIds', ids);
    },
    [setFieldValue],
  );

  const handleSignatureImageUploadSuccess = useCallback(
    (fileUrl: string) => void setFieldValue('signatureImageUri', fileUrl),
    [setFieldValue],
  );

  const handlePictureImageUploadSuccess = useCallback(
    (fileUrl: string) => void setFieldValue('pictureImageUri', fileUrl),
    [setFieldValue],
  );

  const handleDeleteSignatureImage = useCallback(
    () => void setFieldValue('signatureImageUri', EMPTY_STRING),
    [setFieldValue],
  );

  const handleDeletePictureImage = useCallback(
    () => void setFieldValue('pictureImageUri', EMPTY_STRING),
    [setFieldValue],
  );

  const personQueryKeys = useMemo<readonly (readonly string[])[]>(
    () => [[getDocumentName(PersonPage_OnePerson_QueryDocument), values._id]],
    [values._id],
  );

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      {!values.isInternal && (
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <FormControl error={!!(errors.isVip && touched.isVip)} fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={values.isVip}
                  id="third-party-general-info-isVip"
                  name="isVip"
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              }
              label="Tiers VIP"
            />
            {!!(errors.isVip && touched.isVip) && (
              <FormHelperText>{errors.isVip}</FormHelperText>
            )}
          </FormControl>
        </Grid>
      )}
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdMultiSelect<ThirdPartyCategoryId, IdOption<ThirdPartyCategoryId>>
          error={!!(errors.categoryIds && touched.categoryIds)}
          helperText={
            !!(errors.categoryIds && touched.categoryIds) &&
            (getIn(errors, 'categoryIds') as string)
          }
          id="third-party-general-info-category-ids"
          label="Catégories du tiers"
          name="categoryIds"
          nullable
          onBlur={handleBlur}
          onChange={handleCategoryIdsChange}
          options={thirdPartyCategories.map((thirdPartyCategory) => ({
            id: thirdPartyCategory._id,
            label: thirdPartyCategory.label,
          }))}
          value={
            (values.categoryIds as readonly ThirdPartyCategoryId[] | null) ??
            EMPTY_ARRAY
          }
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <IdSelect<CivilityId, IdOption<CivilityId>>
          error={
            !!(getIn(errors, 'civilityId') && getIn(touched, 'civilityId'))
          }
          helperText={
            !!(getIn(errors, 'civilityId') && getIn(touched, 'civilityId')) &&
            (getIn(errors, 'civilityId') as string)
          }
          id="third-party-general-info-civility-id"
          label="Civilité"
          name="civilityId"
          onBlur={handleBlur}
          onChange={handleCivilityIdChange}
          options={civilities.map((civility) => ({
            id: civility._id,
            label: civility.label,
          }))}
          required
          value={values.civilityId as CivilityId}
        />
      </Grid>
      <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={!!(getIn(errors, 'firstName') && getIn(touched, 'firstName'))}
          fullWidth
          helperText={getTouchedError(errors, touched, 'firstName')}
          id="third-party-general-info-first-name"
          label="Prénom"
          name="firstName"
          onBlur={handleBlur}
          onChange={handleFirstNameChange}
          type="text"
          value={values.firstName}
          variant="outlined"
        />
      </Grid>
      <Grid size={{ sm: 6, xs: 12 }} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={!!(getIn(errors, 'lastName') && getIn(touched, 'lastName'))}
          fullWidth
          helperText={getTouchedError(errors, touched, 'lastName')}
          id="third-party-general-info-last-name"
          label="Nom"
          name="lastName"
          onBlur={handleBlur}
          onChange={handleLastNameChange}
          required
          type="text"
          value={values.lastName}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <DatePicker
          displayWeekNumber
          label="Date de naissance"
          onChange={handleBirthDateChange}
          showDaysOutsideCurrentMonth
          slotProps={{
            field: {
              clearable: true,
            },
            actionBar: {
              actions: ['cancel', 'clear', 'today'],
            },
            textField: {
              error: !!getIn(errors, 'birthDate'),
              fullWidth: true,
              helperText: getIn(errors, 'birthDate') as string,
              id: 'birthDate',
              name: 'birthDate',
              onBlur: handleBirthDateBlur,
            },
          }}
          value={birthDateValue}
          views={['year', 'month', 'day']}
        />
      </Grid>
      <Grid
        size={12}
        sx={{ pt: 1, pb: 1, display: 'flex', alignItems: 'center' }}
      >
        {!values.color ? (
          <FormControl error={!!(errors.color && touched.color)} fullWidth>
            <FormControlLabel
              control={
                <Switch
                  checked={!values.color}
                  id="third-party-general-info-auto-color"
                  name="color"
                  onBlur={handleBlur}
                  onChange={switchAutoColor}
                />
              }
              label="Couleur automatique"
            />
            {!!(errors.color && touched.color) && (
              <FormHelperText>{errors.color}</FormHelperText>
            )}
          </FormControl>
        ) : (
          <>
            <TextField
              error={!!(getIn(errors, 'color') && getIn(touched, 'color'))}
              helperText={getTouchedError(errors, touched, 'color')}
              id="third-party-general-info-color"
              label="Couleur"
              name="color"
              onBlur={handleBlur}
              onChange={handleChange}
              sx={{ flexGrow: 1 }}
              type="color"
              value={values.color}
              variant="outlined"
            />
            <Box sx={{ ml: 1 }}>
              <Tooltip title="Générer la couleur automatiquement">
                <IconButton
                  aria-label="Générer la couleur automatiquement"
                  onClick={clearColor}
                >
                  <AutoAwesomeIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </>
        )}
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={
            !!(getIn(errors, 'internalMemo') && getIn(touched, 'internalMemo'))
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'internalMemo')}
          id="third-party-general-info-internalMemo"
          label="Mémo interne"
          multiline
          name="internalMemo"
          onBlur={handleBlur}
          onChange={handleChange}
          rows={4}
          type="text"
          value={values.internalMemo}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 1, pb: 1 }}>
        <TextField
          error={!!(getIn(errors, 'memo') && getIn(touched, 'memo'))}
          fullWidth
          helperText={getTouchedError(errors, touched, 'memo')}
          id="third-party-general-info-memo"
          label="Mémo"
          multiline
          name="memo"
          onBlur={handleBlur}
          onChange={handleChange}
          rows={4}
          type="text"
          value={values.memo}
          variant="outlined"
        />
      </Grid>
      <Grid size={12} sx={{ pt: 2, pb: 1 }}>
        <ImageUploadField
          currentImageUri={values.pictureImageUri}
          entityId={values._id}
          fileCategoryId={thirdPartyFileCategoriesCodeMap.ProfileImages._id}
          fileDirectoryCategoryId={
            fileDirectoryCategoriesCodeMap.ThirdParty._id
          }
          label="Image de profil"
          onDelete={handleDeletePictureImage}
          onUploadSuccess={handlePictureImageUploadSuccess}
          queryKeysToInvalidate={personQueryKeys}
          showPreview
        />
      </Grid>
      {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
      {!!values.isInternal && (
        <Grid size={12} sx={{ pt: 1, pb: 1 }}>
          <ImageUploadField
            currentImageUri={values.signatureImageUri}
            entityId={values._id}
            fileCategoryId={thirdPartyFileCategoriesCodeMap.ProfileImages._id}
            fileDirectoryCategoryId={
              fileDirectoryCategoriesCodeMap.ThirdParty._id
            }
            label="Signature"
            onDelete={handleDeleteSignatureImage}
            onUploadSuccess={handleSignatureImageUploadSuccess}
            queryKeysToInvalidate={personQueryKeys}
            showPreview
          />
        </Grid>
      )}
    </Grid>
  );
};
